import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useUserAccess } from '../../context/UserAccessContext';
import {
  fetchWebCreatorSite,
  updateWebCreatorSite,
} from '../../api/webCreator';
import EditorRightPanel from './components/EditorRightPanel';
import EditorSection from './components/EditorSection';
import EditorPageMenu from './components/EditorPageMenu';
import ComponentSettingsModal from './components/ComponentSettingsModal';
import EditorViewportFrame from './components/EditorViewportFrame';
import SiteConfigModal from './components/SiteConfigModal';
import SiteFontStyles from './components/SiteFontStyles';
import PreviewModal from './components/PreviewModal';
import PublicLinkActions from './components/PublicLinkActions';
import WebCreatorLayout from './WebCreatorLayout';
import { siteHasCommerceAdmin } from './utils/commerce';
import {
  createComponent,
  createDefaultSection,
  migrateSite,
  siteHasMenu,
  VIEWPORT_LIST,
  resolveBackgroundStyle,
  resolveSiteFontStyle,
  SCROLL_PADDING_BOTTOM,
  cloneComponent,
  duplicateComponent,
  getGridPlacement,
  bringComponentToFront,
  sendComponentToBack,
  isComponentPlaneActive,
  isPageMenuPlaneActive,
  DEFAULT_PLANE_FILTER,
} from './utils/defaults';
import { refreshHubupSnapshotsInSite } from './utils/hubupWebCreator';
import { refreshCampaignSnapshotsInSite } from './utils/campaignWebCreator';
import { useEditorGridDrag } from './hooks/useEditorGridDrag';
import {
  coerceJardinsSectionGrid,
  isForestFillImage,
  sectionWithExpandedGridRows,
  siteUsesDesignKit,
} from './utils/designKit';
import {
  applySectionGridRowsForViewport,
  fitSectionGridRowsToVisibleContent,
} from './utils/sectionGrid';
import { JARDINS_MOBILE_LAYOUT_V } from './utils/jardinsResponsive';
import JardinsMobileBookingBar from './components/JardinsMobileBookingBar';
import {
  resolveJardinsFluidScale,
  useElementLayoutWidth,
} from './utils/jardinsFluid';
import { SiteAmbientMusicProvider } from './components/SiteAmbientMusic';
import { ambientMusicForPersist } from './utils/ambientMusic';

export default function WebCreatorEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { chooseAppMode, chooserOpen } = useUserAccess();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [site, setSite] = useState(null);
  const [viewMode, setViewMode] = useState('desktop');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const [selectedPageMenu, setSelectedPageMenu] = useState(false);
  const [settingsTarget, setSettingsTarget] = useState(null);
  const settingsTargetRef = useRef(null);
  settingsTargetRef.current = settingsTarget;
  const [siteConfigOpen, setSiteConfigOpen] = useState(false);
  const [planeFilter, setPlaneFilter] = useState(() => ({ ...DEFAULT_PLANE_FILTER }));
  const [addViewportOnly, setAddViewportOnly] = useState(false);
  const [message, setMessage] = useState('');
  const clipboardRef = useRef(null);
  const editorViewportRef = useRef(null);
  const jardinsDesktopCanvas =
    Boolean(site && siteUsesDesignKit(site, 'jardins') && viewMode === 'desktop');
  const editorCanvasWidth = useElementLayoutWidth(editorViewportRef, jardinsDesktopCanvas);
  const jardinsFluidScale = jardinsDesktopCanvas
    ? resolveJardinsFluidScale(editorCanvasWidth, 'desktop')
    : 1;

  const findComponent = useCallback(
    (componentId) => {
      for (const sec of site?.sections || []) {
        const comp = (sec.components || []).find((c) => c.id === componentId);
        if (comp) return { sectionId: sec.id, component: comp };
      }
      return null;
    },
    [site],
  );

  const handleMoveComponent = useCallback(
    (sectionId, componentId, placement) => {
      setSite((prev) => ({
        ...prev,
        sections: prev.sections.map((sec) => {
          if (sec.id !== sectionId) return sec;
          const comp = (sec.components || []).find((c) => c.id === componentId);
          if (isForestFillImage(comp, sec)) return sec;

          const expanded = sectionWithExpandedGridRows(sec, placement, viewMode);
          const withPlacement = {
            ...expanded,
            components: expanded.components.map((c) => {
              if (c.id !== componentId) return c;
              if (viewMode === 'desktop') {
                return { ...c, ...placement };
              }
              return {
                ...c,
                [viewMode]: {
                  ...(c[viewMode] || {}),
                  colStart: placement.colStart,
                  colSpan: placement.colSpan,
                  row: placement.row,
                  rowSpan: placement.rowSpan,
                },
              };
            }),
          };
          return coerceJardinsSectionGrid(withPlacement);
        }),
      }));
    },
    [viewMode],
  );

  const handleTransferComponent = useCallback(
    (fromSectionId, toSectionId, componentId, placement) => {
      setSite((prev) => {
        let moved = null;
        const without = prev.sections.map((sec) => {
          if (sec.id !== fromSectionId) return sec;
          const comp = (sec.components || []).find((c) => c.id === componentId);
          if (!comp) return sec;
          moved = comp;
          return {
            ...sec,
            components: sec.components.filter((c) => c.id !== componentId),
          };
        });
        if (!moved) return prev;

        const patch =
          viewMode === 'desktop'
            ? placement
            : { [viewMode]: { ...(moved[viewMode] || {}), ...placement } };

        return {
          ...prev,
          sections: without.map((sec) => {
            if (sec.id !== toSectionId) return sec;
            const expanded = sectionWithExpandedGridRows(sec, placement, viewMode);
            const withNew = {
              ...expanded,
              components: [...(expanded.components || []), { ...moved, ...patch }],
            };
            return coerceJardinsSectionGrid(withNew);
          }),
        };
      });
      setActiveSectionId(toSectionId);
      setSelectedComponentId(componentId);
    },
    [viewMode],
  );

  const getSectionComponents = useCallback(
    (sectionId) => site?.sections?.find((s) => s.id === sectionId)?.components || [],
    [site?.sections],
  );

  const {
    registerGrid,
    session: dragSession,
    suppressClickRef,
    startMove,
    startResize,
  } = useEditorGridDrag({
    viewport: viewMode,
    getSectionComponents,
    onMoveComponent: handleMoveComponent,
    onTransferComponent: handleTransferComponent,
  });

  const handleDuplicateComponent = useCallback((sectionId, componentId) => {
    setSite((prev) => {
      let newId = null;
      const sections = prev.sections.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const comp = sec.components.find((c) => c.id === componentId);
        if (!comp) return sec;
        const copy = duplicateComponent(comp);
        newId = copy.id;
        const stacked = bringComponentToFront([...sec.components, copy], copy.id);
        return { ...sec, components: stacked };
      });
      if (newId) {
        setActiveSectionId(sectionId);
        setSelectedComponentId(newId);
      }
      return { ...prev, sections };
    });
  }, []);

  const handlePasteComponent = useCallback(() => {
    const clip = clipboardRef.current;
    if (!clip?.component || !activeSectionId) return;
    const placement = getGridPlacement(clip.component, viewMode);
    const copy = cloneComponent(clip.component, { placement });
    setSite((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) =>
        sec.id === activeSectionId
          ? {
              ...sec,
              components: bringComponentToFront([...(sec.components || []), copy], copy.id),
            }
          : sec,
      ),
    }));
    setSelectedComponentId(copy.id);
    setSelectedPageMenu(false);
  }, [activeSectionId, viewMode]);

  const handleDeleteComponent = useCallback((sectionId, componentId) => {
    setSite((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) =>
        sec.id === sectionId
          ? { ...sec, components: sec.components.filter((c) => c.id !== componentId) }
          : sec,
      ),
    }));
    setSelectedComponentId((prev) => (prev === componentId ? null : prev));
  }, []);

  const handleToggleComponentHidden = useCallback((sectionId, componentId) => {
    setSite((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const components = sec.components.map((c) => {
          if (c.id !== componentId) return c;
          if (viewMode === 'desktop') {
            return { ...c, hidden: !c.hidden };
          }
          const vp = c[viewMode] || {};
          return {
            ...c,
            [viewMode]: { ...vp, hidden: !vp.hidden },
          };
        });
        return fitSectionGridRowsToVisibleContent({ ...sec, components }, viewMode);
      }),
    }));
  }, [viewMode]);

  const handleTogglePageMenuHidden = useCallback(() => {
    setSite((prev) => {
      if (!prev.pageMenu) return prev;
      if (viewMode === 'desktop') {
        return {
          ...prev,
          pageMenu: { ...prev.pageMenu, hidden: !prev.pageMenu.hidden },
        };
      }
      const vp = prev.pageMenu[viewMode] || {};
      return {
        ...prev,
        pageMenu: {
          ...prev.pageMenu,
          [viewMode]: { ...vp, hidden: !vp.hidden },
        },
      };
    });
  }, [viewMode]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (settingsTarget || siteConfigOpen || previewOpen) return;
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;

      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === 'c' && selectedComponentId) {
        const found = findComponent(selectedComponentId);
        if (found) {
          e.preventDefault();
          clipboardRef.current = {
            component: found.component,
            sourceSectionId: found.sectionId,
          };
          setMessage('Composant copié.');
          window.setTimeout(() => setMessage(''), 1800);
        }
        return;
      }

      if (mod && e.key.toLowerCase() === 'v') {
        if (clipboardRef.current?.component && activeSectionId) {
          e.preventDefault();
          handlePasteComponent();
          setMessage('Composant collé.');
          window.setTimeout(() => setMessage(''), 1800);
        }
        return;
      }

      if (mod && e.key.toLowerCase() === 'd' && selectedComponentId) {
        const found = findComponent(selectedComponentId);
        if (found) {
          e.preventDefault();
          handleDuplicateComponent(found.sectionId, found.component.id);
        }
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedComponentId) {
        const found = findComponent(selectedComponentId);
        if (found) {
          e.preventDefault();
          handleDeleteComponent(found.sectionId, found.component.id);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    settingsTarget,
    siteConfigOpen,
    previewOpen,
    selectedComponentId,
    activeSectionId,
    findComponent,
    handlePasteComponent,
    handleDuplicateComponent,
    handleDeleteComponent,
  ]);

  useEffect(() => {
    if (chooserOpen) return;
    chooseAppMode('web-creator');
  }, [chooseAppMode, chooserOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWebCreatorSite(id);
      const s = migrateSite(res?.data?.site);
      setSite(s);
      setActiveSectionId(s?.sections?.[0]?.id || null);
    } catch {
      navigate('/web-creator', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('commerceAdmin') === '1' && id && site && siteHasCommerceAdmin(site)) {
      navigate(`/web-creator/edit/${id}/administration`, { replace: true });
    }
  }, [searchParams, site, id, navigate]);

  useEffect(() => {
    if (!selectedComponentId || !site) return;
    const found = findComponent(selectedComponentId);
    if (found) {
      const sec = site.sections.find((s) => s.id === found.sectionId);
      if (!isComponentPlaneActive(found.component, planeFilter, sec?.components, viewMode)) {
        setSelectedComponentId(null);
      }
    }
    if (selectedPageMenu && !isPageMenuPlaneActive(planeFilter)) {
      setSelectedPageMenu(false);
    }
  }, [planeFilter, selectedComponentId, selectedPageMenu, site, findComponent]);

  const persist = async (patch, successMsg) => {
    setSaving(true);
    setMessage('');
    try {
      const res = await updateWebCreatorSite(id, patch);
      setSite(migrateSite(res?.data?.site));
      if (successMsg) setMessage(successMsg);
      return true;
    } catch (err) {
      setMessage(err.message || 'Erreur lors de la sauvegarde.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const refreshExternalSnapshots = async (draftSite) =>
    refreshCampaignSnapshotsInSite(await refreshHubupSnapshotsInSite(draftSite));

  const buildPersistPayload = (refreshed, extra = {}) => ({
    title: refreshed.title,
    slug: refreshed.slug,
    description: refreshed.description || '',
    ...(refreshed.isTemplate
      ? { templateDescription: refreshed.templateDescription || '' }
      : {}),
    seo: refreshed.seo,
    geo: refreshed.geo,
    sections: refreshed.sections,
    pageMenu: refreshed.pageMenu || null,
    pageBackground: refreshed.pageBackground,
    typography: refreshed.typography,
    ambientMusic: ambientMusicForPersist(refreshed.ambientMusic),
    modules: refreshed.modules,
    clickAndCollect: refreshed.clickAndCollect,
    clickAndCollectOrders: refreshed.clickAndCollectOrders,
    reservation: refreshed.reservation,
    reservationRequests: refreshed.reservationRequests,
    gridVersion: refreshed.gridVersion ?? 3,
    ...(siteUsesDesignKit(refreshed, 'jardins')
      ? {
          jardinsMobileLayoutV:
            refreshed.jardinsMobileLayoutV ?? JARDINS_MOBILE_LAYOUT_V,
        }
      : {}),
    ...extra,
  });

  const handleSave = async () => {
    const refreshed = await refreshExternalSnapshots(site);
    setSite(refreshed);
    await persist(buildPersistPayload(refreshed), 'Brouillon enregistré.');
  };

  const handlePublish = async () => {
    const refreshed = await refreshExternalSnapshots(site);
    setSite(refreshed);
    await persist(
      {
        title: refreshed.title,
        slug: refreshed.slug,
        description: refreshed.description || '',
        seo: refreshed.seo,
        geo: refreshed.geo,
        sections: refreshed.sections,
        pageMenu: refreshed.pageMenu || null,
        pageBackground: refreshed.pageBackground,
        typography: refreshed.typography,
        modules: refreshed.modules,
        clickAndCollect: refreshed.clickAndCollect,
        clickAndCollectOrders: refreshed.clickAndCollectOrders,
        reservation: refreshed.reservation,
        reservationRequests: refreshed.reservationRequests,
        ...buildPersistPayload(refreshed),
        published: true,
      },
      'Site publié !',
    );
  };

  const handleUnpublish = async () => {
    if (
      !window.confirm(
        'Dépublier ce site ? Il ne sera plus accessible publiquement.',
      )
    ) {
      return;
    }
    const refreshed = await refreshExternalSnapshots(site);
    setSite(refreshed);
    await persist(
      {
        title: refreshed.title,
        slug: refreshed.slug,
        description: refreshed.description || '',
        seo: refreshed.seo,
        geo: refreshed.geo,
        sections: refreshed.sections,
        pageMenu: refreshed.pageMenu || null,
        pageBackground: refreshed.pageBackground,
        typography: refreshed.typography,
        published: false,
      },
      'Site dépublié.',
    );
  };

  const updateSections = (nextSections) => {
    setSite((prev) => ({ ...prev, sections: nextSections }));
  };

  const handleAdd = (type) => {
    if (type === 'section' || type.startsWith('section-')) {
      const sectionType = type === 'section' ? 'normal' : type.replace('section-', '');
      const section = createDefaultSection(site.sections.length, sectionType);
      updateSections([...site.sections, section]);
      setActiveSectionId(section.id);
      setSelectedPageMenu(false);
      return;
    }

    if (type === 'menu') {
      if (siteHasMenu(site)) return;
      const viewports =
        addViewportOnly && viewMode ? [viewMode] : undefined;
      const menu = createComponent('menu', [], { viewports });
      setSite((prev) => ({ ...prev, pageMenu: menu }));
      setSelectedPageMenu(true);
      setSelectedComponentId(null);
      return;
    }

    if (!activeSectionId) return;
    const viewports =
      addViewportOnly && viewMode ? [viewMode] : undefined;
    const next = site.sections.map((sec) => {
      if (sec.id !== activeSectionId) return sec;
      const comp = createComponent(type, sec.components || [], { viewports });
      return { ...sec, components: [...(sec.components || []), comp] };
    });
    updateSections(next);
    const added = next
      .find((s) => s.id === activeSectionId)
      ?.components?.slice(-1)[0];
    if (added) {
      setSelectedComponentId(added.id);
      setSelectedPageMenu(false);
    }
  };

  const handleDeletePageMenu = () => {
    setSite((prev) => ({ ...prev, pageMenu: null }));
    setSelectedPageMenu(false);
  };

  const handleDeleteSection = (sectionId) => {
    const next = site.sections.filter((s) => s.id !== sectionId);
    updateSections(next);
    if (activeSectionId === sectionId) setActiveSectionId(next[0]?.id || null);
  };

  const handleResizeSection = useCallback((sectionId, gridRows, viewport = 'desktop') => {
    setSite((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const withRows = applySectionGridRowsForViewport(s, viewport, gridRows);
        return coerceJardinsSectionGrid(withRows, { syncBleedImage: viewport === 'desktop' });
      }),
    }));
  }, []);

  const handleBringToFront = useCallback((sectionId, componentId) => {
    setSite((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) =>
        sec.id === sectionId
          ? { ...sec, components: bringComponentToFront(sec.components || [], componentId) }
          : sec,
      ),
    }));
  }, []);

  const handleSendToBack = useCallback((sectionId, componentId) => {
    setSite((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) =>
        sec.id === sectionId
          ? { ...sec, components: sendComponentToBack(sec.components || [], componentId) }
          : sec,
      ),
    }));
  }, []);

  const handleMenuLayoutChange = useCallback(({ target, placement }) => {
    setSite((prev) => {
      const menu = prev.pageMenu;
      if (!menu?.props) return prev;
      const props = menu.props;
      if (target === 'logo') {
        return {
          ...prev,
          pageMenu: {
            ...menu,
            props: { ...props, logoMenuPlacement: placement },
          },
        };
      }
      return {
        ...prev,
        pageMenu: {
          ...menu,
          props: {
            ...props,
            tabPlacements: {
              ...(props.tabPlacements || {}),
              [target]: placement,
            },
          },
        },
      };
    });
  }, []);

  const handleSettingsSave = (draft) => {
    if (settingsTarget.kind === 'section') {
      updateSections(
        site.sections.map((s) => (s.id === settingsTarget.sectionId ? { ...s, ...draft } : s)),
      );
    } else if (settingsTarget.kind === 'pageMenu') {
      setSite((prev) => ({ ...prev, pageMenu: draft }));
    } else {
      updateSections(
        site.sections.map((sec) => {
          if (sec.id !== settingsTarget.sectionId) return sec;
          return {
            ...sec,
            components: sec.components.map((c) => (c.id === draft.id ? draft : c)),
          };
        }),
      );
    }
    setSettingsTarget(null);
  };

  const handleSettingsLiveUpdate = useCallback((draft) => {
    const target = settingsTargetRef.current;
    if (!target) return;
    setSite((prev) => {
      if (target.kind === 'section') {
        return {
          ...prev,
          sections: prev.sections.map((s) =>
            s.id === target.sectionId ? { ...s, ...draft } : s,
          ),
        };
      }
      if (target.kind === 'pageMenu') {
        return { ...prev, pageMenu: draft };
      }
      if (target.kind === 'component') {
        return {
          ...prev,
          sections: prev.sections.map((sec) => {
            if (sec.id !== target.sectionId) return sec;
            const withComp = {
              ...sec,
              components: sec.components.map((c) => (c.id === draft.id ? draft : c)),
            };
            return fitSectionGridRowsToVisibleContent(withComp, viewMode);
          }),
        };
      }
      return prev;
    });
  }, [viewMode]);

  const handleSiteConfigSave = async (draft) => {
    const ambientMusic = ambientMusicForPersist(draft.ambientMusic);
    const isTemplate = site?.isTemplate;
    const patch = {
      title: draft.title,
      slug: draft.slug,
      description: draft.description || '',
      ...(isTemplate ? { templateDescription: draft.templateDescription || '' } : {}),
      pageBackground: draft.pageBackground,
      seo: draft.seo,
      geo: draft.geo,
      typography: draft.typography,
      ambientMusic,
    };
    setSite((prev) => ({
      ...prev,
      ...patch,
      description: draft.description,
      templateDescription: draft.templateDescription,
    }));
    const saved = await persist(patch, 'Configuration enregistrée.');
    if (saved) setSiteConfigOpen(false);
  };

  const openCommerceAdmin = () => {
    if (id) navigate(`/web-creator/edit/${id}/administration`);
  };

  if (loading || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Icon name="Loader2" size={20} className="animate-spin mr-2" />
        Chargement de l&apos;éditeur…
      </div>
    );
  }

  const pageStyle = {
    ...resolveBackgroundStyle(site.pageBackground, '#ffffff'),
    ...resolveSiteFontStyle(site.typography),
  };
  const editorViewportStyle =
    jardinsDesktopCanvas ? { '--wc-jardins-fluid': jardinsFluidScale } : undefined;

  return (
    <WebCreatorLayout>
    <SiteFontStyles typography={site.typography} />
    <div className="h-full flex flex-col bg-neutral-200/50 min-h-0">
      <header className="relative shrink-0 border-b border-border bg-card px-4 py-3 z-30 flex items-center justify-between gap-3 min-h-[56px]">
        <div className="flex items-center gap-2 min-w-0 z-[1]">
          <Link to="/web-creator" className="p-2 rounded-lg hover:bg-muted shrink-0" title="Retour">
            <Icon name="ArrowLeft" size={18} />
          </Link>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate leading-tight flex items-center gap-2">
              <span className="truncate">{site.title || 'Sans titre'}</span>
              {site.isTemplate ? (
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-400">
                  Template
                </span>
              ) : null}
            </p>
            {(site.isTemplate ? site.templateDescription || site.description : site.description) ? (
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {site.isTemplate
                  ? site.templateDescription || site.description
                  : site.description}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground/50 italic mt-0.5">Aucune description</p>
            )}
            {site.slug && !site.isTemplate ? (
              <PublicLinkActions
                slug={site.slug}
                published={site.published}
                size="sm"
                className="mt-1.5"
              />
            ) : null}
          </div>
        </div>

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] flex flex-col items-center gap-1.5">
          <div className="flex items-center justify-center rounded-lg border border-border overflow-hidden shrink-0 bg-card shadow-sm">
            {VIEWPORT_LIST.map((vp) => (
              <button
                key={vp.id}
                type="button"
                className={`px-3 py-1.5 text-xs whitespace-nowrap ${
                  viewMode === vp.id ? 'bg-primary text-primary-foreground' : 'bg-background'
                }`}
                onClick={() => setViewMode(vp.id)}
              >
                {vp.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap z-[1]">
          {!site.isTemplate && siteHasCommerceAdmin(site) ? (
            <>
              <div className="hidden sm:flex items-center justify-center rounded-lg border border-border overflow-hidden shrink-0 bg-card shadow-sm">
                <span className="px-3 py-1.5 text-xs whitespace-nowrap bg-primary text-primary-foreground">
                  Éditeur
                </span>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs whitespace-nowrap bg-background hover:bg-muted/80 transition-colors"
                  onClick={openCommerceAdmin}
                >
                  Administration
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="sm:hidden"
                onClick={openCommerceAdmin}
              >
                <Icon name="Store" size={16} className="mr-1.5" />
                Administration
              </Button>
            </>
          ) : null}
          <Button variant="outline" onClick={() => setSiteConfigOpen(true)} size="sm">
            <Icon name="Settings" size={16} className="mr-1.5" />
            Config
          </Button>
          <Button variant="outline" onClick={() => setPreviewOpen(true)} size="sm">
            <Icon name="Eye" size={16} className="mr-1.5" />
            Aperçu
          </Button>
          <Button variant="outline" onClick={handleSave} disabled={saving} size="sm">
            {saving ? '…' : 'Enregistrer'}
          </Button>
          {!site.isTemplate ? (
            site.published ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleUnpublish}
                  disabled={saving}
                  size="sm"
                  className="text-amber-700 border-amber-200 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-900/50"
                >
                  Dépublier
                </Button>
              </>
            ) : (
              <Button onClick={handlePublish} disabled={saving} size="sm">
                Publier
              </Button>
            )
          ) : null}
          <span
            className={`hidden md:inline-flex text-[10px] uppercase tracking-wide px-2 py-1 rounded-full ${
              site.isTemplate
                ? 'bg-violet-500/10 text-violet-700 dark:text-violet-400'
                : site.published
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground'
            }`}
          >
            {site.isTemplate ? 'Template' : site.published ? 'En ligne' : 'Brouillon'}
          </span>
        </div>
      </header>

      {message && (
        <div className="px-4 py-2 text-sm bg-primary/10 text-primary border-b border-primary/20">
          {message}
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        <EditorViewportFrame
          viewMode={viewMode}
          pageStyle={pageStyle}
          viewportStyle={editorViewportStyle}
          viewportRef={editorViewportRef}
        >
          <SiteAmbientMusicProvider
            config={site.ambientMusic}
            siteSlug={site.slug}
            playbackEnabled
          >
          <main
            className={`min-h-full ${siteUsesDesignKit(site, 'jardins') ? 'wc-site--jardins' : ''}`}
            onClick={(e) => {
              if (e.target.closest('[data-grid-component]')) return;
              if (e.target.closest('[data-page-menu]')) return;
              setSelectedComponentId(null);
              setSelectedPageMenu(false);
            }}
          >
            {site.pageMenu && (
              <EditorPageMenu
                menu={site.pageMenu}
                sections={site.sections}
                site={site}
                viewport={viewMode}
                siteTypography={site.typography}
                planeFilter={planeFilter}
                selected={selectedPageMenu}
                onSelect={() => {
                  setSelectedPageMenu(true);
                  setSelectedComponentId(null);
                }}
                onOpenSettings={setSettingsTarget}
                onDelete={handleDeletePageMenu}
                onMenuLayoutChange={handleMenuLayoutChange}
              />
            )}

            {site.sections.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center px-4">
                <Icon name="Layers" size={36} className="text-muted-foreground/30 mb-4" />
                <p className="text-sm text-muted-foreground max-w-sm">
                  Ajoutez une section (couleur, image ou vidéo) pour commencer — panneau Composants à droite.
                </p>
              </div>
            ) : (
              site.sections.map((section) => (
                <EditorSection
                  key={section.id}
                  section={section}
                  sections={site.sections}
                  viewport={viewMode}
                  siteTypography={site.typography}
                  site={site}
                  selectedSectionId={activeSectionId}
                  selectedComponentId={selectedComponentId}
                  dragSession={dragSession}
                  registerGrid={registerGrid}
                  suppressClickRef={suppressClickRef}
                  onStartMove={startMove}
                  onStartResize={startResize}
                  onSelectSection={(id) => {
                    setActiveSectionId(id);
                    setSelectedPageMenu(false);
                  }}
                  onSelectComponent={(compId, sectionId) => {
                    setSelectedComponentId(compId);
                    setActiveSectionId(sectionId);
                    setSelectedPageMenu(false);
                  }}
                  onOpenSettings={setSettingsTarget}
                  onDeleteComponent={handleDeleteComponent}
                  onDeleteSection={handleDeleteSection}
                  onDuplicateComponent={handleDuplicateComponent}
                  onResizeSection={handleResizeSection}
                  onBringToFront={handleBringToFront}
                  onSendToBack={handleSendToBack}
                  planeFilter={planeFilter}
                  jardinsFluidScale={jardinsFluidScale}
                />
              ))
            )}
            {siteUsesDesignKit(site, 'jardins') ? (
              <JardinsMobileBookingBar
                sections={site.sections}
                interactive
                viewport={viewMode}
              />
            ) : null}
            <div style={{ height: SCROLL_PADDING_BOTTOM, minHeight: 320 }} aria-hidden />
          </main>
          </SiteAmbientMusicProvider>
        </EditorViewportFrame>

        <EditorRightPanel
          activeSectionId={activeSectionId}
          hasMenu={siteHasMenu(site)}
          viewMode={viewMode}
          addViewportOnly={addViewportOnly}
          onAddViewportOnlyChange={setAddViewportOnly}
          onAdd={handleAdd}
          planeFilter={planeFilter}
          onPlaneFilterChange={setPlaneFilter}
        />
      </div>

      <ComponentSettingsModal
        target={settingsTarget}
        sections={site.sections}
        siteTypography={site.typography}
        viewport={viewMode}
        onClose={() => setSettingsTarget(null)}
        onSave={handleSettingsSave}
        onLiveUpdate={handleSettingsLiveUpdate}
      />

      <SiteConfigModal
        site={site}
        open={siteConfigOpen}
        onClose={() => setSiteConfigOpen(false)}
        onSave={handleSiteConfigSave}
        onSiteRepaired={(next) => setSite(migrateSite(next))}
      />

      {previewOpen && (
        <PreviewModal
          site={site}
          viewport={viewMode}
          onViewportChange={setViewMode}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
    </WebCreatorLayout>
  );
}
